import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Events, Properties, Referrers } from '../../../../enums/Analytics';
import { isVanityUrl } from '../../../../lib/helper/RoomHelper';
import Analytics, { EventProperty } from '../../../../tools/Analytics';
import AnonymousIdentityGuard from '../../View/AnonymousIdentityGuard';
import RoomsInterface from '../../../../graphql/Rooms/RoomsInterface';
import ScheduleCallButton from './ScheduleCallButton';
import SnackbarStore from '../../../../stores/observables/SnackbarStore';
import ThemableButton from '../../../../theme/components/Button/ThemableButton';
import ThemableTextField from '../../../../theme/components/TextField/ThemableTextField';
import { useI18n } from '../../../../lib/hook/useI18n';

interface Props {
  contactId?: string;
  disabled?: boolean;
  roomId?: string;
  trackingProperties?: EventProperty;
}

const ScheduleCall: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useI18n();

  const {
    contactId,
    disabled = false,
    roomId,
    trackingProperties = {},
  } = props;

  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const toggleOpen = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const onConfirmClick = async () => {
    if (!roomId) {
      return;
    }

    try {
      setScheduling(true);

      await new RoomsInterface(contactId).scheduleCall({
        message: message.trim(),
        roomId,
      });

      SnackbarStore.show(t('schedule.modal.success'));
      setMessage('');
      setOpen(false);
      Analytics.track(Events.SCHEDULE_CALL_REQUESTED, {
        ...trackingProperties,
        [Properties.VANITY_URL]: isVanityUrl(window.location.href),
        [Properties.SHARED_AVAILABILITY]: !!message.trim(),
      });
    } catch (e) {
      SnackbarStore.show(e as string);
    } finally {
      setScheduling(false);
    }
  };

  return (
    <>
      <AnonymousIdentityGuard
        referrer={Referrers.DEAL_ROOMS.ACTIVITY.SCHEDULE_CALL}
      >
        <ScheduleCallButton fullWidth onClick={toggleOpen} size="small" />
      </AnonymousIdentityGuard>
      <Dialog fullWidth maxWidth="sm" onClose={toggleOpen} open={open}>
        <DialogTitle>{t('schedule.modal.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('schedule.modal.body')}</DialogContentText>
          <ThemableTextField
            autoFocus
            disabled={scheduling}
            fullWidth
            multiline
            onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessage(ev.target.value)
            }
            placeholder={t('schedule.modal.input_text')}
            rows={3}
            value={message}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={scheduling} onClick={toggleOpen}>
            {t('schedule.modal.cancel')}
          </Button>
          <ThemableButton
            disabled={scheduling}
            onClick={onConfirmClick}
            variant="contained"
          >
            {t('schedule.modal.button')}
          </ThemableButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleCall;
